// Projects list + Project detail
import React from 'react';
import { Icon, Pyramid } from './icons.jsx';
import { PROJECTS } from './data.js';

const STATUS_TABS = ['All', 'Active', 'Paused', 'Done'];
const PRI_TABS = ["All", "High", "Medium", "Low", "Parked", "Queued"];
const TYPE_TABS = ["All", "Work", "Life"];

const ProjectsView = ({ onOpen }) => {
  const [status, setStatus] = React.useState("Active");
  const [pri, setPri] = React.useState("All");
  const [type, setType] = React.useState("All");
  const [sort, setSort] = React.useState("Priority");
  const [view, setViewMode] = React.useState("list"); // list | board

  const list = PROJECTS.filter(p => {
    if (type !== "All" && p.type !== type.toLowerCase()) return false;
    if (pri !== "All" && p.priority !== pri) return false;
    return true;
  });

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1 className="page__title">Active <em>projects</em></h1>
          <div className="page__sub">
            <span className="mono">8 projects</span>
            <span style={{ margin: "0 10px", color: "var(--ink-5)" }}>·</span>
            <span>32 cycles scheduled this week · <span style={{ color: "var(--success)" }}>14 logged</span></span>
          </div>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <button className="btn btn--outline btn--sm">
            <Icon name="folder" size={13} /><span>New folder</span>
          </button>
          <button className="btn btn--accent">
            <Icon name="plus" size={14} /><span>New project</span>
          </button>
        </div>
      </div>

      <div className="filterbar">
        <div className="tabs">
          {STATUS_TABS.map(s => (
            <button key={s} data-active={status === s ? "1" : "0"} onClick={() => setStatus(s)}>{s}</button>
          ))}
        </div>
        <div className="tabs">
          {TYPE_TABS.map(s => (
            <button key={s} data-active={type === s ? "1" : "0"} onClick={() => setType(s)}>{s}</button>
          ))}
        </div>
        <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
          {PRI_TABS.map(p => (
            <button
              key={p}
              className="chip"
              data-active={pri === p ? "1" : "0"}
              onClick={() => setPri(p)}
              style={pri === p ? { background: "var(--ink-1)", color: "var(--surface)", borderColor: "transparent" } : null}
            >
              {p !== "All" && <span className="dot" style={{
                background: p === "High" ? "var(--danger)" : p === "Medium" ? "var(--warning)" : p === "Low" ? "var(--accent)" : "var(--ink-4)"
              }}></span>}
              {p}
            </button>
          ))}
        </div>

        <div className="row" style={{ marginLeft: "auto", gap: 8 }}>
          <span className="mono" style={{ fontSize: 11, color: "var(--ink-3)" }}>Sort</span>
          <select className="select" style={{ width: "auto", padding: "6px 28px 6px 10px", fontSize: 12.5 }}
            value={sort} onChange={e => setSort(e.target.value)}>
            <option>Priority</option>
            <option>Last touched</option>
            <option>Progress</option>
            <option>Name</option>
          </select>
          <div className="tabs">
            <button data-active={view === "list" ? "1" : "0"} onClick={() => setViewMode("list")}>
              <Icon name="menu" size={13} />
            </button>
            <button data-active={view === "board" ? "1" : "0"} onClick={() => setViewMode("board")}>
              <Icon name="grid" size={13} />
            </button>
          </div>
        </div>
      </div>

      {view === "list" ? <ProjectList list={list} onOpen={onOpen} /> : <ProjectBoard list={list} onOpen={onOpen} />}
    </div>
  );
};

const ProjectList = ({ list, onOpen }) => (
  <div className="card">
    <div className="prow" style={{
      background: "var(--surface-2)",
      fontFamily: "var(--font-mono)", fontSize: 10.5, letterSpacing: "0.12em",
      textTransform: "uppercase", color: "var(--ink-3)", padding: "9px 24px"
    }}>
      <span></span>
      <span>Project</span>
      <span>Category</span>
      <span>Priority</span>
      <span>Cycles</span>
      <span>Progress</span>
      <span></span>
    </div>
    <div className="plist">
      {list.map(p => {
        const pct = Math.round((p.logged / p.target) * 100);
        return (
          <button key={p.id} className="prow" onClick={() => onOpen(p)} style={{ textAlign: "left", cursor: "pointer" }}>
            <span className="pdot" style={{ background: p.color }}></span>
            <div className="prow__title">
              <div className="t">
                {p.title.split(p.em).map((part, i, arr) => (
                  <React.Fragment key={i}>
                    {part}
                    {i < arr.length - 1 && <em>{p.em}</em>}
                  </React.Fragment>
                ))}
              </div>
              <div className="s">{p.summary}</div>
            </div>
            <span className="prow__cat">{p.category}</span>
            <span className={
              "pri " +
              (p.priority === "High" ? "pri--high" : p.priority === "Medium" ? "pri--med" : p.priority === "Low" ? "pri--low" : p.priority === "Parked" ? "pri--park" : "pri--queue")
            }>
              {p.priority}
            </span>
            <span className="mono" style={{ fontSize: 12, color: "var(--ink-2)" }}>
              {p.logged}<span style={{ color: "var(--ink-4)" }}>/{p.target}</span>
            </span>
            <div className="prow__progress">
              <span className="v"><span className="tnum">{pct}%</span> · {p.weekly}/wk</span>
              <div className="bar"><i style={{ width: pct + "%", background: p.color }}></i></div>
            </div>
            <button className="iconbtn" onClick={e => e.stopPropagation()}>
              <Icon name="more" size={14} />
            </button>
          </button>
        );
      })}
    </div>
  </div>
);

const ProjectBoard = ({ list, onOpen }) => {
  const cols = {
    "Active This Week": list.slice(0, 3),
    "Active This Quarter": list.slice(3, 6),
    "Paused / Waiting": list.slice(6, 8),
  };
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
      {Object.entries(cols).map(([col, items]) => (
        <div key={col}>
          <div className="row" style={{ marginBottom: 10, padding: "0 4px" }}>
            <span className="mono" style={{ fontSize: 10.5, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--ink-3)" }}>{col}</span>
            <span className="chip" style={{ marginLeft: "auto" }}>{items.length}</span>
          </div>
          <div className="col" style={{ gap: 10 }}>
            {items.map(p => {
              const pct = Math.round((p.logged / p.target) * 100);
              return (
                <button key={p.id} className="card card--hov" onClick={() => onOpen(p)} style={{ textAlign: "left", padding: 16, display: "flex", flexDirection: "column", gap: 8, cursor: "pointer" }}>
                  <div className="row" style={{ gap: 8 }}>
                    <span className="pdot pdot--lg" style={{ background: p.color }}></span>
                    <span className={"pri " + (p.priority === "High" ? "pri--high" : p.priority === "Medium" ? "pri--med" : "pri--low")}>{p.priority}</span>
                    <span className="mono" style={{ marginLeft: "auto", fontSize: 10.5, color: "var(--ink-3)" }}>{p.lastTouched}</span>
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 500, letterSpacing: "-0.01em" }}>
                    {p.title.split(p.em).map((part, i, arr) => (
                      <React.Fragment key={i}>{part}{i < arr.length - 1 && <em>{p.em}</em>}</React.Fragment>
                    ))}
                  </div>
                  <div style={{ fontSize: 12.5, color: "var(--ink-3)" }}>{p.summary}</div>
                  <div className="bar" style={{ marginTop: 4 }}><i style={{ width: pct + "%", background: p.color }}></i></div>
                  <div className="row" style={{ gap: 10, marginTop: 2 }}>
                    <span className="mono" style={{ fontSize: 11, color: "var(--ink-2)" }}>{p.logged}/{p.target} cycles · {pct}%</span>
                    <span className="mono" style={{ marginLeft: "auto", fontSize: 11, color: "var(--ink-3)" }}>{p.tasks} tasks</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

// PROJECT DETAIL
const ProjectDetail = ({ project, onBack }) => {
  const p = project || PROJECTS[0];
  const [tab, setTab] = React.useState("Action items");
  const tabs = ["Action items", "Segments", "Sessions", "Notes", "Files"];
  const pct = Math.round((p.logged / p.target) * 100);

  return (
    <div className="page">
      <button className="btn btn--ghost btn--sm" onClick={onBack} style={{ marginBottom: 16, marginLeft: -8 }}>
        <Icon name="chev_left" size={13} /><span>All projects</span>
      </button>

      <div className="page__head" style={{ alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}>
          <div className="row" style={{ gap: 10, marginBottom: 10 }}>
            <span className="pdot pdot--lg" style={{ background: p.color, width: 18, height: 18 }}></span>
            <span className={"pri " + (p.priority === "High" ? "pri--high" : p.priority === "Medium" ? "pri--med" : "pri--low")}>{p.priority}</span>
            <span className="chip">{p.category}</span>
            <span className="chip"><Icon name="github" size={11} /> ed/projectos</span>
          </div>
          <h1 className="page__title">
            {p.title.split(p.em).map((part, i, arr) => (
              <React.Fragment key={i}>{part}{i < arr.length - 1 && <em>{p.em}</em>}</React.Fragment>
            ))}
          </h1>
          <div className="page__sub" style={{ maxWidth: 720 }}>{p.summary}</div>
        </div>
        <div className="row" style={{ gap: 10 }}>
          <button className="btn btn--outline btn--sm"><Icon name="sparkles" size={13} /><span>AI organize</span></button>
          <button className="btn btn--outline btn--sm"><Icon name="edit" size={13} /><span>Edit</span></button>
          <button className="btn btn--accent"><Icon name="plus" size={13} /><span>New task</span></button>
        </div>
      </div>

      {/* Stats strip */}
      <div className="kpis" style={{ gridTemplateColumns: "repeat(5, 1fr)" }}>
        <div className="kpi">
          <span className="kpi__lbl">Cycles logged</span>
          <span className="kpi__val tnum">{p.logged}<span style={{ color: "var(--ink-4)", fontSize: 18 }}>/{p.target}</span></span>
          <span className="kpi__delta kpi__delta--up">+{p.weekly} this week</span>
          <div className="kpi__bar"><i style={{ width: pct + "%", background: p.color }}></i></div>
        </div>
        <div className="kpi">
          <span className="kpi__lbl">This week</span>
          <span className="kpi__val tnum">{Math.min(p.weekly, 4)}<span style={{ color: "var(--ink-4)", fontSize: 18 }}>/{p.weekly}</span></span>
          <span className="kpi__delta">cycles target</span>
        </div>
        <div className="kpi">
          <span className="kpi__lbl">Segments</span>
          <span className="kpi__val tnum">{p.milestones}</span>
          <span className="kpi__delta">2 done · 2 open</span>
        </div>
        <div className="kpi">
          <span className="kpi__lbl">Tasks</span>
          <span className="kpi__val tnum">{p.tasks}</span>
          <span className="kpi__delta">7 in progress</span>
        </div>
        <div className="kpi">
          <span className="kpi__lbl">Last touched</span>
          <span className="kpi__val" style={{ fontSize: 22 }}>{p.lastTouched}</span>
          <span className="kpi__delta">today's session at C9</span>
        </div>
      </div>

      <div className="filterbar" style={{ borderBottom: "1px solid var(--hair)", paddingBottom: 0, gap: 0, marginTop: 8 }}>
        {tabs.map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding: "10px 16px",
            fontSize: 13,
            fontWeight: 500,
            color: tab === t ? "var(--ink-1)" : "var(--ink-3)",
            borderBottom: tab === t ? "2px solid var(--accent)" : "2px solid transparent",
            marginBottom: -1,
            transition: "color var(--t-fast), border-color var(--t-fast)"
          }}>{t}</button>
        ))}
      </div>

      {tab === "Action items" && <TasksTab project={p} />}
      {tab === "Segments" && <SegmentsTab project={p} />}
      {tab === "Sessions" && <SessionsTab project={p} />}
      {tab === "Notes" && <NotesTab project={p} />}
      {tab === "Files" && <FilesTab project={p} />}
    </div>
  );
};

const SAMPLE_TASKS = [
  { id: 1, title: "Wire onboarding form states (loading, error, success)", milestone: "Onboarding flow", est: 1, status: "Done", today: false, priority: "High", ai: false },
  { id: 2, title: "Smoke-test public signup → enroll → onboarding", milestone: "Onboarding flow", est: 1, status: "In Progress", today: true, priority: "High", ai: false },
  { id: 3, title: "Audit empty states on every list view", milestone: "Polish pass", est: 2, status: "Open", today: false, priority: "Medium", ai: true },
  { id: 4, title: "Write copy for paywall — three plans, single CTA", milestone: "Polish pass", est: 1, status: "Open", today: false, priority: "Medium", ai: true },
  { id: 5, title: "Hook up Resend transactional email", milestone: "Email", est: 2, status: "Open", today: false, priority: "Low", ai: false },
  { id: 6, title: "Decide on weekly digest cadence — Sunday vs Monday", milestone: "Email", est: 1, status: "Blocked", today: false, priority: "Low", ai: false },
  { id: 7, title: "Add motion on cycle-complete (320ms collapse)", milestone: "Polish pass", est: 1, status: "Done", today: false, priority: "Low", ai: false },
];

const TasksTab = ({ project }) => {
  const [tasks, setTasks] = React.useState(SAMPLE_TASKS);
  const groups = {};
  tasks.forEach(t => { (groups[t.milestone] = groups[t.milestone] || []).push(t); });
  const toggle = id => setTasks(ts => ts.map(t => t.id === id ? { ...t, status: t.status === "Done" ? "Open" : "Done" } : t));
  return (
    <div style={{ marginTop: 24 }}>
      <div className="proposal" style={{ marginBottom: 20 }}>
        <div className="row" style={{ marginBottom: 6 }}>
          <span className="spark"><Icon name="sparkles" size={11} />AI proposal</span>
          <span className="mono" style={{ marginLeft: "auto", fontSize: 11, color: "var(--ink-3)" }}>3 new tasks · drafted just now</span>
        </div>
        <div style={{ fontSize: 13.5, color: "var(--ink-2)", marginBottom: 10 }}>
          Based on your description, I'd add <strong style={{ color: "var(--ink-1)" }}>3 polish tasks</strong> under "Polish pass" — empty states, loading states, and a 320ms cycle-complete animation. Estimated 4 cycles total.
        </div>
        <div className="row" style={{ gap: 8 }}>
          <button className="btn btn--accent btn--sm">Accept all</button>
          <button className="btn btn--outline btn--sm">Review one-by-one</button>
          <button className="btn btn--ghost btn--sm">Dismiss</button>
        </div>
      </div>

      {Object.entries(groups).map(([m, items]) => (
        <div key={m} className="card" style={{ marginBottom: 16 }}>
          <div className="card__head">
            <Icon name="flag" size={13} />
            <div className="card__title">{m}</div>
            <span className="card__sub">{items.filter(t => t.status === "Done").length} / {items.length} done</span>
            <button className="btn btn--ghost btn--sm" style={{ marginLeft: "auto" }}>
              <Icon name="sparkles" size={12} /><span>Break this down</span>
            </button>
          </div>
          {items.map((t, i) => (
            <div key={t.id} className="row" style={{
              padding: "12px 24px",
              borderTop: "1px solid var(--hair)",
              gap: 12
            }}>
              <button className="iconbtn" style={{ width: 18, height: 18, color: "var(--ink-4)", cursor: "grab" }}><Icon name="drag" size={14} /></button>
              <button className="tick" data-checked={t.status === "Done" ? "1" : "0"} onClick={() => toggle(t.id)}>
                <Icon name="check" size={11} stroke={2.6} />
              </button>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="row" style={{ gap: 8 }}>
                  <span style={{
                    fontSize: 13.5, fontWeight: 450,
                    color: t.status === "Done" ? "var(--ink-3)" : "var(--ink-1)",
                    textDecoration: t.status === "Done" ? "line-through" : "none"
                  }}>{t.title}</span>
                  {t.today && <span className="chip chip--accent" style={{ fontSize: 10 }}><Icon name="target" size={10} />Top 3</span>}
                  {t.ai && <span className="chip" style={{ fontSize: 10, color: "var(--accent)", borderColor: "var(--accent-soft)" }}><Icon name="sparkles" size={10} />AI</span>}
                </div>
              </div>
              <span className={"pri " + (t.priority === "High" ? "pri--high" : t.priority === "Medium" ? "pri--med" : "pri--low")} style={{ fontSize: 9.5 }}>{t.priority}</span>
              <span className="mono" style={{ fontSize: 11, color: "var(--ink-3)", width: 64, textAlign: "right" }}>{t.est} cycle{t.est > 1 ? "s" : ""}</span>
              <span className="chip" style={{ fontSize: 10,
                color: t.status === "In Progress" ? "var(--info)" : t.status === "Blocked" ? "var(--danger)" : "var(--ink-3)",
                background: t.status === "In Progress" ? "var(--info-soft)" : t.status === "Blocked" ? "var(--danger-soft)" : "var(--surface-2)",
                borderColor: "transparent"
              }}>{t.status}</span>
              <button className="iconbtn"><Icon name="more" size={13} /></button>
            </div>
          ))}
          <div className="row" style={{ padding: "10px 24px", borderTop: "1px solid var(--hair)", color: "var(--ink-3)" }}>
            <Icon name="plus" size={13} />
            <span style={{ fontSize: 12.5 }}>Add task</span>
          </div>
        </div>
      ))}
    </div>
  );
};

const SegmentsTab = ({ project }) => {
  const segs = [
    { t: "Onboarding flow", d: "End-to-end: signup → entitlement → first project", status: "Open", due: "May 14", n: 6, done: 2 },
    { t: "Polish pass", d: "Empty/loading/error states across every list", status: "Open", due: "May 21", n: 8, done: 0 },
    { t: "Email", d: "Resend integration + transactional + weekly digest", status: "Open", due: "May 28", n: 4, done: 0 },
    { t: "Beta launch", d: "Invite first 10 testers, gather feedback", status: "Open", due: "Jun 1", n: 0, done: 0 },
  ];
  return (
    <div style={{ marginTop: 24 }} className="col">
      {segs.map((s, i) => {
        const pct = s.n ? Math.round((s.done / s.n) * 100) : 0;
        return (
          <div key={i} className="card card--hov" style={{ padding: 20, display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{
              width: 44, height: 44, borderRadius: 10,
              display: "grid", placeItems: "center",
              background: "var(--surface-2)",
              fontFamily: "var(--font-mono)", fontWeight: 600, fontSize: 15, color: "var(--ink-2)"
            }}>{String(i + 1).padStart(2, "0")}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 500, letterSpacing: "-0.01em" }}>{s.t}</div>
              <div style={{ fontSize: 12.5, color: "var(--ink-3)", marginTop: 2 }}>{s.d}</div>
            </div>
            <div style={{ minWidth: 120 }}>
              <div className="mono" style={{ fontSize: 11, color: "var(--ink-3)" }}>{s.done}/{s.n} tasks</div>
              <div className="bar" style={{ marginTop: 4 }}><i style={{ width: pct + "%", background: project.color }}></i></div>
            </div>
            <span className="chip"><Icon name="calendar" size={11} />{s.due}</span>
            <button className="btn btn--ghost btn--sm"><Icon name="sparkles" size={12} /><span>Break down</span></button>
          </div>
        );
      })}
      <button className="card" style={{ padding: 14, color: "var(--ink-3)", borderStyle: "dashed", textAlign: "center", fontSize: 13 }}>
        <Icon name="plus" size={13} /> &nbsp; Add segment
      </button>
    </div>
  );
};

const SessionsTab = ({ project }) => (
  <div style={{ marginTop: 24 }}>
    <div className="card" style={{ padding: 24 }}>
      <div className="row" style={{ marginBottom: 16 }}>
        <h3 style={{ fontSize: 14, fontWeight: 500, margin: 0 }}>Last 7 days</h3>
        <span className="mono" style={{ fontSize: 11, color: "var(--ink-3)", marginLeft: "auto" }}>{project.weekly} cycles · 6h target</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 6, marginBottom: 12 }}>
        {[2, 3, 1, 0, 2, 1, 1].map((v, i) => (
          <div key={i} style={{ textAlign: "center" }}>
            <div style={{
              height: 48,
              background: v === 0 ? "var(--surface-2)" : project.color,
              opacity: v === 0 ? 1 : (0.4 + v * 0.2),
              borderRadius: 6,
              display: "grid", placeItems: "end center",
              padding: 4,
              color: v === 0 ? "var(--ink-4)" : "white",
              fontFamily: "var(--font-mono)", fontSize: 11, fontWeight: 600
            }}>{v || ""}</div>
            <div className="mono" style={{ fontSize: 10, color: "var(--ink-3)", marginTop: 4, letterSpacing: "0.04em" }}>
              {["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"][i]}
            </div>
          </div>
        ))}
      </div>
    </div>

    <h3 style={{ fontSize: 13, fontWeight: 500, color: "var(--ink-2)", margin: "24px 0 12px" }}>Recent sessions</h3>
    <div className="card">
      {[
        { d: "Today · C9", t: "Smoke-test public signup → enroll → onboarding", s: "In Progress", n: 1 },
        { d: "Today · C2", t: "Wire onboarding form states (loading, error, success)", s: "Completed", n: 1 },
        { d: "Yesterday · C9", t: "Pipeline launch checklist groundwork", s: "Completed", n: 2 },
        { d: "Yesterday · C3", t: "Define plan tiers + entitlement contract", s: "Completed", n: 1 },
        { d: "May 6 · C9", t: "Wire CCOS handoff stub", s: "Completed", n: 2 },
      ].map((r, i) => (
        <div key={i} className="row" style={{ padding: "12px 24px", borderTop: i === 0 ? 0 : "1px solid var(--hair)", gap: 16 }}>
          <span className="mono" style={{ fontSize: 11, color: "var(--ink-3)", width: 130 }}>{r.d}</span>
          <span className="cycle__stripe" style={{ background: project.color, height: 16, width: 3, borderRadius: 2 }}></span>
          <span style={{ flex: 1, fontSize: 13, color: "var(--ink-1)" }}>{r.t}</span>
          <span className="mono" style={{ fontSize: 11, color: "var(--ink-3)" }}>{r.n} cycle{r.n > 1 ? "s" : ""}</span>
          <span className={"chip " + (r.s === "Completed" ? "chip--success" : "chip--accent")} style={{ fontSize: 10 }}>
            {r.s === "Completed" && <Icon name="check" size={9} stroke={2.6} />}
            {r.s}
          </span>
        </div>
      ))}
    </div>
  </div>
);

const NotesTab = ({ project }) => (
  <div style={{ marginTop: 24, display: "grid", gridTemplateColumns: "1fr 320px", gap: 24 }}>
    <div>
      <h3 style={{ fontSize: 13, fontWeight: 500, color: "var(--ink-2)", margin: "0 0 8px" }}>Next steps</h3>
      <textarea className="textarea" rows={4} defaultValue="• Smoke test the public signup flow end to end this morning&#10;• Decide email cadence — Sunday digest vs Monday?&#10;• Ping Carter about typography license for the launch site" />

      <h3 style={{ fontSize: 13, fontWeight: 500, color: "var(--ink-2)", margin: "24px 0 8px" }}>Description</h3>
      <textarea className="textarea" rows={6} defaultValue="Pipeline is the lead-capture module of the Successful Citizen Suite. v1 ships at citizenpipeline.com, public beta. Goals: capture, enrich, score, route — opinionated, defaults-first, no config tax. The launch needs onboarding, billing handoff to CCOS, weekly digest, and a credibility-grade marketing surface." />
    </div>
    <div>
      <h3 style={{ fontSize: 13, fontWeight: 500, color: "var(--ink-2)", margin: "0 0 8px" }}>Activity</h3>
      <div className="card" style={{ padding: 12 }}>
        {[
          { who: "You", t: "completed", on: "Wire onboarding form states", w: "2h" },
          { who: "AI", t: "drafted", on: "3 polish tasks", w: "5h" },
          { who: "You", t: "moved", on: "Email digest to next segment", w: "Yesterday" },
          { who: "GitHub", t: "synced", on: "issue #42 closed", w: "2d" },
        ].map((a, i) => (
          <div key={i} className="row" style={{ padding: "8px 4px", borderTop: i === 0 ? 0 : "1px solid var(--hair)", gap: 8, fontSize: 12 }}>
            <span style={{ fontWeight: 500, color: a.who === "AI" ? "var(--accent)" : a.who === "GitHub" ? "var(--ink-2)" : "var(--ink-1)" }}>{a.who}</span>
            <span style={{ color: "var(--ink-3)" }}>{a.t}</span>
            <span style={{ color: "var(--ink-1)", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.on}</span>
            <span className="mono" style={{ fontSize: 10.5, color: "var(--ink-4)" }}>{a.w}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const FilesTab = ({ project }) => (
  <div style={{ marginTop: 24 }} className="col">
    <div className="card" style={{ padding: 16 }}>
      <div className="row">
        <Icon name="github" size={16} />
        <span style={{ fontWeight: 500 }}>ed/projectos</span>
        <span className="chip">main</span>
        <span className="mono" style={{ marginLeft: "auto", fontSize: 11, color: "var(--ink-3)" }}>synced 14m ago</span>
      </div>
    </div>
    {[
      { n: "app/today/page.tsx", s: "12 KB", t: "TypeScript" },
      { n: "lib/cycles.ts", s: "8 KB", t: "TypeScript" },
      { n: "components/ui/cycle-row.tsx", s: "6 KB", t: "TypeScript" },
      { n: "styles/pipeline.css", s: "26 KB", t: "CSS" },
      { n: "drizzle/schema.ts", s: "14 KB", t: "TypeScript" },
    ].map((f, i) => (
      <div key={i} className="row" style={{ padding: "10px 16px", border: "1px solid var(--hair)", borderRadius: 7, background: "var(--surface)", gap: 12 }}>
        <Icon name="folder" size={14} />
        <span style={{ fontFamily: "var(--font-mono)", fontSize: 12.5 }}>{f.n}</span>
        <span className="chip" style={{ marginLeft: "auto", fontSize: 10 }}>{f.t}</span>
        <span className="mono" style={{ fontSize: 11, color: "var(--ink-3)" }}>{f.s}</span>
      </div>
    ))}
  </div>
);

export { ProjectsView, ProjectDetail };
